import type { KeyboardEvent } from 'react';

/**
 * Clavier d'une barre d'onglets, modèle « tabs » de WAI-ARIA (RGAA 7.1,
 * TF-341) : flèches gauche et droite d'un onglet à l'autre (en boucle),
 * Origine et Fin pour le premier et le dernier ; l'onglet atteint s'ouvre
 * aussitôt. À poser sur l'élément `role="tablist"`, dont chaque onglet porte
 * `data-onglet` et `tabIndex` 0 s'il est ouvert, -1 sinon : la touche Tab
 * entre dans la barre sur l'onglet ouvert et en sort au suivant.
 */
export function clavierOnglets<T extends string>(
  cles: readonly T[],
  actif: T,
  onChanger: (cle: T) => void,
): (evenement: KeyboardEvent<HTMLElement>) => void {
  return (evenement) => {
    if (cles.length === 0) return;
    const rang = Math.max(0, cles.indexOf(actif));
    const cible =
      evenement.key === 'ArrowRight'
        ? (rang + 1) % cles.length
        : evenement.key === 'ArrowLeft'
          ? (rang - 1 + cles.length) % cles.length
          : evenement.key === 'Home'
            ? 0
            : evenement.key === 'End'
              ? cles.length - 1
              : null;
    if (cible === null) return;
    evenement.preventDefault();
    const cle = cles[cible]!;
    const barre = evenement.currentTarget;
    const etiquette = barre.getAttribute('aria-label');
    const viser = (racine: ParentNode | null): void => {
      const onglet = racine?.querySelector<HTMLElement>(`[data-onglet="${cle}"]`);
      onglet?.focus();
      // Une barre qui défile de côté amène l'onglet entier dans la vue.
      onglet?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    };
    onChanger(cle);
    viser(barre);
    // Une barre redessinée avec l'onglet ouvert (chaque section rend la
    // sienne) : on y retrouve l'onglet après le rendu.
    requestAnimationFrame(() => {
      if ((document.activeElement as HTMLElement | null)?.dataset['onglet'] === cle) return;
      viser(etiquette ? document.querySelector(`[role="tablist"][aria-label="${etiquette}"]`) : null);
    });
  };
}

/** Attributs d'un onglet ; `idPanneau` relie l'onglet au contenu qu'il ouvre. */
export function attributsOnglet(
  cle: string,
  selectionne: boolean,
  idPanneau?: string,
): {
  role: 'tab';
  'aria-selected': boolean;
  tabIndex: number;
  'data-onglet': string;
  id?: string;
  'aria-controls'?: string;
} {
  return {
    role: 'tab',
    'aria-selected': selectionne,
    tabIndex: selectionne ? 0 : -1,
    'data-onglet': cle,
    ...(idPanneau ? { id: `${idPanneau}-onglet-${cle}`, 'aria-controls': idPanneau } : {}),
  };
}

/** Attributs du contenu de l'onglet ouvert, à poser sur un conteneur existant. */
export function attributsPanneau(
  id: string,
  actif: string,
): { role: 'tabpanel'; id: string; 'aria-labelledby': string } {
  return { role: 'tabpanel', id, 'aria-labelledby': `${id}-onglet-${actif}` };
}
